import { NavigationProp, useIsFocused, useNavigation } from '@react-navigation/native';
import React, { useEffect, useState } from 'react';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { useAuth } from '../../../context/Auth';
import { AppAgentParamList } from '../../../typings/Params';
import { useQuery } from '@tanstack/react-query';
import { appAgentService } from '../../../services/central-api/app-agent';
import { UserNotInUnit } from '../../../services/central-api/base-service';
import { SafeAreaView } from 'react-native-safe-area-context';
import { ActivityIndicator, FlatList, View } from 'react-native';
import MyAppText from '../../../components/MyAppText';
import { translate } from '../../../services/translate';
import FloatingButton from '../../../components/FloatingButton';
import { faLocationDot } from '@fortawesome/free-solid-svg-icons';
import PolicePatrolScheduleCard from './PolicePatrolScheduleCard';
import { faClipboard } from '@fortawesome/pro-regular-svg-icons';
import ActivityAndServiceCard from './ActivityAndServiceCard';

export default function PolicePatrolSchedule() {
    const { styles, theme } = useStyles(styleSheet);
    const { user: authenticatedUser } = useAuth();
    const navigation = useNavigation<NavigationProp<AppAgentParamList>>();

    const [userNotInUnit, setUserNotInUnit] = useState<boolean>(false);

    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['getPolicePatrolSchedules'],
        queryFn: () => appAgentService.getPolicePatrolSchedules(),
        enabled: !!authenticatedUser && isFocused,
    });

    const { data, error } = queryResult;

    const activitiesAndServices = useQuery({
        queryKey: ['getActivitiesAndServices'],
        queryFn: () => appAgentService.getActivitiesAndServices(),
        enabled: !!authenticatedUser && isFocused,
    });

    const isEnabled = !!authenticatedUser && isFocused;
    const isLoading = isEnabled && (queryResult.isFetching || activitiesAndServices.isFetching);

    const { data: activities } = activitiesAndServices;

    useEffect(() => {
        setUserNotInUnit(error instanceof UserNotInUnit);
    }, [error]);

    function getLastOdometer(item: PolicePatrolSchedule) {
        if (item.endDate && item.odometer) {
            return undefined;
        }

        if (data) {
            for (const i of data) {
                if (i.odometer) {
                    return i.odometer;
                }
            }
        }

        return undefined;
    }

    function getListData() {
        const result: (PolicePatrolSchedule | ActivityAndService)[] = [];
        if (data) {
            result.push(...data);
        }

        if (activities) {
            result.push(...activities.filter(ac => !ac.policePatrolScheduleId));
        }

        result.sort((a, b) => {
            const dateA = new Date(a.startDate).getTime();
            const dateB = new Date(b.startDate).getTime();

            return dateB - dateA;
        });

        return result;
    }

    return (
        <SafeAreaView style={{ flex: 1 }}>
            {isLoading
                ? <View style={{ flexDirection: 'column', flex: 1, justifyContent: 'center', alignItems: 'center' }}>
                    <ActivityIndicator size='large' color={theme.colors.iconColor} />
                </View>
                : <>
                    <FlatList
                        data={getListData()}
                        keyExtractor={(item) => {
                            const isPoliceSchedule = 'activityId' in item;
                            return `${translate(isPoliceSchedule ? 'policePatrolScheduleAcronym' : 'activityAndServiceAcronym')}_${String(item.id)}`;
                        }}
                        contentContainerStyle={[styles.list, data?.length ? {} : { flex: 1 }]}
                        renderItem={({ item }) => {
                            const isPoliceSchedule = 'activityId' in item;

                            if (isPoliceSchedule) {
                                return <PolicePatrolScheduleCard
                                    activity={item}
                                    onEdit={() => {
                                        navigation.navigate('PolicePatrolScheduleDetails', {
                                            activityId: item.id,
                                            odometer: getLastOdometer(item)
                                        });
                                    }}
                                    onFinalize={() => navigation.navigate('PolicePatrolScheduleDetails', {
                                        activityId: item.id,
                                        finalize: true,
                                        odometer: getLastOdometer(item)
                                    })}
                                />;
                            }

                            return <ActivityAndServiceCard
                                activity={item}
                                onEdit={() => {
                                    navigation.navigate('ActivitiesAndServicesDetails', {
                                        activityId: item.id,
                                    });
                                }}
                            />;
                        }}
                        showsVerticalScrollIndicator={false}
                        ItemSeparatorComponent={() => <View style={{ height: 10 }} />}
                        ListEmptyComponent={
                            <View style={styles.emptyList}>
                                {userNotInUnit
                                    ? <MyAppText style={styles.emptyTitle}>{translate('joinUnitToUseThisScreen')}</MyAppText>
                                    : <>
                                        <MyAppText style={styles.emptyTitle}>{translate('noActivityRegistered')}</MyAppText>
                                        <MyAppText style={styles.emptyText}>{translate('useTheButtonToAddTheFirstOne')}</MyAppText>
                                    </>
                                }
                            </View>
                        }
                    />
                </>}
            <FloatingButton
                visible={!userNotInUnit && !isLoading}
                offset={{ horizontal: 14, vertical: 32 }}
                actions={[
                    {
                        name: 'addRas',
                        label: translate('registerSingleActivityAndService'),
                        icon: faClipboard,
                        onPress: () => {
                            navigation.navigate('ActivitiesAndServicesDetails', {});
                        }
                    }, {
                        name: 'createActivity',
                        label: translate('startDps'),
                        icon: faLocationDot,
                        onPress: () => {
                            navigation.navigate('PolicePatrolScheduleDetails', {
                                odometer: data?.at(0)?.odometer ?? undefined,
                            });
                        }
                    }
                ]}
            />
        </SafeAreaView>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    list: {
        paddingTop: 16,
        paddingBottom: 96,
        paddingHorizontal: 16
    },
    emptyList: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center'
    },
    fab: {
        position: 'absolute',
        right: 20,
        bottom: 24,
        width: 56,
        height: 56,
        borderRadius: 28,
        backgroundColor: theme.colors.primaryButton.background,
        alignItems: 'center',
        justifyContent: 'center',
        shadowColor: '#000',
        shadowOpacity: 0.2,
        shadowRadius: 8,
        shadowOffset: { width: 0, height: 4 },
        elevation: 4,
    },
    emptyTitle: {
        fontSize: 18,
        fontWeight: '700',
        flexWrap: 'wrap',
        color: theme.colors.modal.header,
        textAlign: 'center'
    },
    emptyText: {
        fontSize: 16,
        fontWeight: '400',
        flexWrap: 'wrap',
        color: theme.colors.modal.subheader,
        textAlign: 'center'
    },
}));
